import { HISTORY_RANGES, type HistoryRange, type FlowSeriesRange, type ResourceFlows, type StockHistory, type StockRates } from "../../shared/api.js";
import type { Stack } from "../../shared/stack.js";
import type { AppContext } from "../Page.js";
import { h } from "../dom.js";
import { amount, duration, perHour, stackDetail, stackName, time } from "../format.js";
import { Dialog } from "./Dialog.js";
import { toggleFavorite } from "./Favorite.js";
import { flowChart } from "./FlowChart.js";
import { movesTable, UnitToggle, windowsTable } from "./FlowBits.js";
import { historyChart } from "./HistoryChart.js";
import { stackIcon } from "./StackIcon.js";

/** "Runs out in 3h 20m", or why it doesn't. */
export function runOut(rates: StockRates | null, stock: number | null): string {
  if (!rates) return "Not enough history";
  if (stock === 0) return "Empty";
  if (rates.emptyAt === null) return rates.netPerHour > 0 ? "Rising" : "Steady";
  return `Empty in ${duration(rates.emptyAt - Date.now())}`;
}

export function rateStats(rates: StockRates | null, stack: Stack, stock: number | null): HTMLElement {
  if (!rates) return h("p", { class: "muted small" }, "Rates need at least 5 minutes of history while the game is connected.");
  return h(
    "dl",
    { class: "stats" },
    h("dt", null, "Produced"),
    h("dd", null, perHour(rates.prodPerHour, stack.type, false)),
    h("dt", null, "Used"),
    h("dd", null, perHour(rates.usePerHour, stack.type, false)),
    h("dt", null, "Net"),
    h("dd", null, perHour(rates.netPerHour, stack.type)),
    h("dt", null, "Below min"),
    h("dd", null, rates.belowMinAt ? `in ${duration(rates.belowMinAt - Date.now())}` : "not at this rate"),
    h("dt", null, "Runs out"),
    h("dd", null, runOut(rates, stock)),
    h("dt", null, "Measured over"),
    h("dd", null, duration(rates.coveredHours * 3_600_000)),
  );
}

/** A resource's stock chart over a chosen range, with its production and use rates. */
export function openResource(ctx: AppContext, key: string, stack: Stack, initial: HistoryRange = "24h"): void {
  const dialog = new Dialog(stackName(stack), true);
  const ranges = h("div", { class: "segmented", role: "group", "aria-label": "Range" });
  const content = h("div", null, h("p", { class: "muted" }, "Loading…"));
  const flows = h("div");
  dialog.body.append(h("div", { class: "stack-name" }, stackIcon(stack, "lg"), h("span", { class: "muted small" }, stackDetail(stack))), ranges, content, flows);

  const load = async (range: HistoryRange) => {
    for (const b of ranges.querySelectorAll("button")) b.classList.toggle("active", b.dataset.range === range);
    let data: StockHistory;
    try {
      data = await ctx.api.history(key, range);
    } catch (err) {
      content.replaceChildren(h("p", { class: "error" }, (err as Error).message));
      return;
    }
    showUntrack(data);
    const to = Date.now();
    const changes = data.points.slice(-50).reverse();
    content.replaceChildren(
      h(
        "div",
        null,
        historyChart({ stack, points: data.points, from: to - HISTORY_RANGES[range], to, min: data.min, target: data.target }),
        h(
          "p",
          null,
          h("strong", null, `Now ${amount(data.stock, stack.type)}`),
          h("span", { class: "muted" }, data.ruleId === null ? " · monitored without a rule, never crafted" : ` · min ${amount(data.min, stack.type)} · target ${amount(data.target, stack.type)}`),
        ),
        rateStats(data.rates, stack, data.stock),
        changes.length
          ? h(
              "details",
              null,
              h("summary", null, "Samples as a table"),
              h(
                "table",
                { class: "compact" },
                h("thead", null, h("tr", null, h("th", null, "When"), h("th", { class: "num" }, "Amount"))),
                h("tbody", null, ...changes.map(([at, v]) => h("tr", null, h("td", null, time(at)), h("td", { class: "num" }, amount(v, stack.type))))),
              ),
            )
          : null,
      ),
    );
  };
  for (const range of Object.keys(HISTORY_RANGES) as HistoryRange[]) {
    ranges.append(h("button", { type: "button", class: "small", "data-range": range, onclick: () => void load(range) }, range));
  }
  const star = h("button", { type: "button", class: "star-button", onclick: () => void toggleFavorite(ctx, key, stack).then(syncStar) });
  const syncStar = () => {
    const on = ctx.live.favorites.has(key);
    star.textContent = on ? "★ Favorite" : "☆ Add to favorites";
    star.classList.toggle("on", on);
    star.title = on ? "Pinned to the top of every list and watched live. Click to unpin." : "Pin to the top of every list and watch it live";
  };
  syncStar();
  const showUntrack = (data: StockHistory) => {
    const tree = h(
      "button",
      {
        type: "button",
        title: "Everything its craft takes, from the loaded patterns",
        onclick: () => {
          dialog.close();
          ctx.navigate(`patterns/dependencies/${encodeURIComponent(key)}`);
        },
      },
      "Ingredient tree",
    );
    dialog.footer.replaceChildren(star, tree);
    if (!data.tracked) return;
    const stop = h(
      "button",
      {
        type: "button",
        onclick: async () => {
          stop.disabled = true;
          try {
            await ctx.api.untrack(key);
            ctx.toast(data.ruleId === null ? `Stopped monitoring ${stackName(stack)}` : `Stopped monitoring ${stackName(stack)} on its own; its rule keeps it on the home page`);
            dialog.close();
          } catch (err) {
            stop.disabled = false;
            ctx.toast((err as Error).message, "error");
          }
        },
      },
      "Stop monitoring",
    );
    dialog.footer.append(stop);
  };
  dialog.open();
  void load(initial);
  void loadFlows(ctx, key, stack, flows);
}

/** Production and use from every recorded change, per window and per /s /m /h /d, then the changes themselves. */
async function loadFlows(ctx: AppContext, key: string, stack: Stack, el: HTMLElement): Promise<void> {
  let data: ResourceFlows;
  try {
    data = await ctx.api.resourceFlows(key, 100);
  } catch (err) {
    el.replaceChildren(h("p", { class: "error small" }, (err as Error).message));
    return;
  }
  const moves = [...data.moves];
  const table = h("div");
  const unit = new UnitToggle(() => renderTable());
  const renderTable = () => table.replaceChildren(windowsTable(data.windows, stack, unit.unit));
  renderTable();
  const list = h("div", null, movesTable(moves, stack));
  const more = h("button", { type: "button", class: "small", hidden: moves.length < 100 }, "Show older");
  more.addEventListener("click", async () => {
    more.disabled = true;
    try {
      const page = await ctx.api.resourceFlows(key, 200, moves[moves.length - 1].at);
      moves.push(...page.moves);
      list.replaceChildren(movesTable(moves, stack));
      more.hidden = page.moves.length < 200;
    } catch (err) {
      ctx.toast((err as Error).message, "error");
    }
    more.disabled = false;
  });
  const chart = h("div");
  const spans = h("div", { class: "segmented flow-spans", role: "group", "aria-label": "Chart span" });
  const SPANS: Array<[FlowSeriesRange, string, string]> = [
    ["6h", "6 h", "Last 6 hours, per 5 minutes"],
    ["48h", "48 h", "Last 48 hours, per hour"],
    ["30d", "30 d", "Last 30 days, per day"],
  ];
  const loadChart = async (range: FlowSeriesRange) => {
    for (const b of spans.querySelectorAll("button")) b.classList.toggle("active", b.dataset.range === range);
    try {
      chart.replaceChildren(flowChart(await ctx.api.flowSeries(key, range), stack, range === "30d"));
    } catch (err) {
      chart.replaceChildren(h("p", { class: "error small" }, (err as Error).message));
    }
  };
  for (const [range, text, title] of SPANS) {
    spans.append(h("button", { type: "button", class: "small", "data-range": range, title, onclick: () => void loadChart(range) }, text));
  }
  if (data.watched || moves.length) void loadChart("48h");
  el.replaceChildren(
    h("h3", null, "Production and use"),
    h(
      "p",
      { class: "muted small" },
      data.watched
        ? `Every change the Butler reports is recorded and kept ${data.keptDays} day${data.keptDays === 1 ? "" : "s"} (hourly totals for a year). Averages divide by the time it was watched live.`
        : "Not watched live, so its changes are not recorded. Add it to favorites (or give it a rule) to record every change.",
    ),
    data.watched || moves.length ? h("div", null, spans, chart) : "",
    unit.el,
    table,
    moves.length
      ? h("details", null, h("summary", null, `Every change (latest ${moves.length})`), list, more)
      : h("p", { class: "muted small" }, "No change recorded yet."),
  );
}
